import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Loader2, Package, Ticket, ShoppingBag, ArrowLeft, Truck, Download, CalendarCheck, Box, Utensils } from "lucide-react";
import api from "@/lib/api";
import ChangeAddOnsModal from "@/components/ChangeAddOnsModal";
import { money } from "@/lib/format";

const KIND_META = {
  order: { label: "Order", Icon: ShoppingBag, tint: "text-[#144F75] bg-[#144F75]/10" },
  ticket: { label: "Event ticket", Icon: Ticket, tint: "text-[#9E5C7F] bg-[#9E5C7F]/10" },
  package: { label: "Package", Icon: Package, tint: "text-[#5C8A72] bg-[#5C8A72]/15" },
};

const CARRIER_URL = {
  usps: (t) => `https://tools.usps.com/go/TrackConfirmAction?tLabels=${t}`,
  ups: (t) => `https://www.ups.com/track?tracknum=${t}`,
  fedex: (t) => `https://www.fedex.com/fedextrack/?trknbr=${t}`,
  dhl: (t) => `https://www.dhl.com/en/express/tracking.html?AWB=${t}`,
};
const trackUrl = (carrier, t) =>
  (CARRIER_URL[(carrier || "").toLowerCase()] || ((x) => `https://www.google.com/search?q=${encodeURIComponent(`${carrier || ""} ${x}`)}`))(t);

// A single order line rendered by its own item type — no shipping language for
// tickets, packages, services, digital or pickup-only goods.
function OrderItemAction({ it, oid }) {
  if (it.ticket_id) {
    return (
      <Link to={`/ticket/${it.ticket_id}`} data-testid={`history-open-ticket-${it.ticket_id}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#9E5C7F] hover:underline">
        <Ticket size={14} /> Open ticket / QR{it.title ? ` · ${it.title}` : ""}
      </Link>
    );
  }
  if (it.kind === "package") {
    return (
      <Link to="/dashboard" data-testid={`history-view-package-${oid}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5C8A72] hover:underline">
        <CalendarCheck size={14} /> Book a session{it.title ? ` · ${it.title}` : ""}
      </Link>
    );
  }
  if (it.type === "digital") {
    return it.digital_url ? (
      <a href={it.digital_url} target="_blank" rel="noreferrer" data-testid={`history-access-digital-${oid}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#144F75] hover:underline">
        <Download size={14} /> Access {it.title || "digital item"}
      </a>
    ) : (
      <span className="inline-flex items-center gap-1.5 text-sm text-[#4A5D6B]" data-testid={`history-digital-pending-${oid}`}>
        <Download size={14} /> {it.title || "Digital item"} — access sent by email
      </span>
    );
  }
  // Physical product
  if (it.tracking_number) {
    return (
      <a href={trackUrl(it.carrier, it.tracking_number)} target="_blank" rel="noreferrer"
        data-testid={`history-track-${oid}`}
        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5C8A72] hover:underline">
        <Truck size={14} /> Track {it.title ? `· ${it.title}` : "order"} ({(it.carrier || "").toUpperCase()} {it.tracking_number})
      </a>
    );
  }
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-[#4A5D6B]" data-testid={`history-preparing-${oid}`}>
      <Box size={14} /> {it.title || "Item"} — preparing to ship
    </span>
  );
}

export default function OrderHistory() {
  const [changeReg, setChangeReg] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["order-history"],
    queryFn: () => api.get("/me/order-history").then((r) => r.data.history || []),
  });

  return (
    <div className="max-w-2xl mx-auto px-6 lg:px-10 py-12" data-testid="order-history-page">
      <Link to="/dashboard" className="text-sm text-[#144F75] inline-flex items-center gap-1 mb-6 hover:underline">
        <ArrowLeft size={14} /> Back to dashboard
      </Link>
      <h1 className="font-serif text-3xl md:text-4xl text-[#1A242B] mb-2">Order history</h1>
      <p className="text-[#4A5D6B] mb-8 text-sm">Everything you've purchased — products, packages and event tickets.</p>

      {isLoading ? (
        <div className="grid place-items-center py-24"><Loader2 className="animate-spin text-[#144F75]" size={28} /></div>
      ) : !data || data.length === 0 ? (
        <div className="eyw-card p-10 text-center" data-testid="order-history-empty">
          <ShoppingBag className="mx-auto mb-3 text-[#8FA89B]" size={32} />
          <p className="text-[#4A5D6B]">You haven't made any purchases yet.</p>
          <Link to="/marketplace" className="eyw-btn mt-5 inline-flex">Browse the marketplace</Link>
        </div>
      ) : (
        <ul className="space-y-4">
          {data.map((h) => {
            const meta = KIND_META[h.kind] || KIND_META.order;
            const { Icon } = meta;
            const items = Array.isArray(h.items) ? h.items : [];
            return (
              <li key={`${h.kind}-${h.id}`} className="eyw-card p-5" data-testid={`history-item-${h.id}`}>
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-start gap-3">
                    <span className={`shrink-0 grid place-items-center w-9 h-9 rounded-full ${meta.tint}`}><Icon size={16} /></span>
                    <div>
                      <div className="text-sm font-semibold text-[#1A242B]" data-testid={`history-title-${h.id}`}>{h.title}</div>
                      <div className="text-xs text-[#4A5D6B] mt-0.5">
                        <span className="uppercase tracking-wide">{meta.label}</span>
                        {h.date ? ` · ${String(h.date).slice(0, 10)}` : ""}
                        {h.kind === "ticket" && h.event_date ? ` · event ${h.event_date}` : ""}
                        {h.kind === "package" && h.expires_at ? ` · expires ${String(h.expires_at).slice(0, 10)}` : ""}
                      </div>
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    {h.total != null && <div className="text-sm font-serif text-[#144F75]">{money(Number(h.total))}</div>}
                    <span className="text-[11px] px-2 py-0.5 rounded-full bg-[#F1F5F8] text-[#4A5D6B] mt-1 inline-block">{h.status}</span>
                  </div>
                </div>

                {/* Direct event ticket → open QR pass */}
                {h.kind === "ticket" && (
                  <div className="mt-3 pl-12 flex flex-wrap items-center gap-x-5 gap-y-1.5">
                    <Link to={`/ticket/${h.id}`} data-testid={`history-open-ticket-${h.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#9E5C7F] hover:underline">
                      <Ticket size={14} /> Open ticket / QR
                    </Link>
                    {["confirmed", "pending"].includes(h.status) && h.event_date >= new Date().toISOString().slice(0, 10) && (
                      <button onClick={() => setChangeReg(h.id)} data-testid={`history-change-addons-${h.id}`}
                        className="inline-flex items-center gap-1.5 text-sm font-medium text-[#144F75] hover:underline">
                        <Utensils size={14} /> Change add-ons{(h.meal_option || h.lodging_option) ? ` · ${[h.meal_option, h.lodging_option].filter(Boolean).join(", ")}` : ""}
                      </button>
                    )}
                  </div>
                )}

                {/* Direct package credit → remaining uses + redeem */}
                {h.kind === "package" && (
                  <div className="mt-3 pl-12">
                    <Link to="/dashboard" data-testid={`history-book-package-${h.id}`}
                      className="inline-flex items-center gap-1.5 text-sm font-medium text-[#5C8A72] hover:underline">
                      <CalendarCheck size={14} /> Book a session with this package
                    </Link>
                  </div>
                )}

                {/* Marketplace order → each line rendered by its own type */}
                {h.kind === "order" && items.length > 0 && (
                  <div className="mt-3 pl-12 space-y-1.5" data-testid={`history-order-lines-${h.id}`}>
                    {items.map((it, idx) => (
                      <div key={idx}><OrderItemAction it={it} oid={`${h.id}-${idx}`} /></div>
                    ))}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}
      {changeReg && <ChangeAddOnsModal registrationId={changeReg} onClose={() => setChangeReg(null)} />}
    </div>
  );
}
